import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';
import AuthContext from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
// ⚠ Temporal: selector de paleta mientras se decide cuál se queda. Ver src/theme/.
import { ThemeProvider } from './theme/ThemeContext';

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ClientSelfRegisterPage from './pages/ClientSelfRegisterPage';
import PublicCatalogPage from './pages/PublicCatalogPage';
import DashboardPage from './pages/DashboardPage';
import SalesPage from './pages/SalesPage';
import QuickPrintPage from './pages/QuickPrintPage';
import SettingsPage from './pages/SettingsPage';
import ProductsPage from './pages/ProductsPage';
import ClientsPage from './pages/ClientsPage';
import SuppliersPage from './pages/SuppliersPage';
import ExpensesPage from './pages/ExpensesPage';
import IncomesPage from './pages/IncomesPage';
import PaymentNotificationsPage from './pages/PaymentNotificationsPage';
import UsersPage from './pages/UsersPage';
import InvoicePage from './pages/InvoicePage';
import QuotesPage from './pages/QuotesPage';
import QuotePage from './pages/QuotePage';
import ExpenseReceiptPage from './pages/ExpenseReceiptPage';
import SupplierSettlementsPage from './pages/SupplierSettlementsPage';
import CashRegisterPage from './pages/CashRegisterPage';
import SettlementReceiptPage from './pages/SettlementReceiptPage';
import DeliverySlipPage from './pages/DeliverySlipPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import DpaPage from './pages/DpaPage';
import LandingPage from './pages/LandingPage';
import PlansComparisonPage from './pages/PlansComparisonPage';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Footer from './components/Footer';
import ImpersonationBanner from './components/ImpersonationBanner';
import CookieConsentBanner from './components/CookieConsentBanner';

// Páginas de negocio nuevas: siempre lazy (.ai/rules/frontend.md). Las importadas arriba
// de forma estática son deuda conocida (PERF-FE-02).
const ProductionBoardPage = React.lazy(() => import('./pages/ProductionBoardPage'));

// Layout no envuelve su <Outlet /> en Suspense: cada ruta lazy trae el suyo.
const pageFallback = <div className="text-center py-5"><Spinner animation="border" /></div>;

// Admin Pages (Lazy Loaded)
const AdminLayout = React.lazy(() => import('./components/layouts/AdminLayout'));
const AdminDashboardPage = React.lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminBusinessesPage = React.lazy(() => import('./pages/admin/AdminBusinessesPage'));
const AdminPlansPage = React.lazy(() => import('./pages/admin/AdminPlansPage'));
const AdminContactMessagesPage = React.lazy(() => import('./pages/admin/AdminContactMessagesPage'));
const AdminImpersonationLogsPage = React.lazy(() => import('./pages/admin/AdminImpersonationLogsPage'));
const AdminAiAnalysesPage = React.lazy(() => import('./pages/admin/AdminAiAnalysesPage'));
// ⚠ Temporal: tema de la plataforma mientras se elige la paleta. Ver src/theme/.
const AdminAppearancePage = React.lazy(() => import('./pages/admin/AdminAppearancePage'));

/**
 * @function InvoiceRedirect
 * @description Redirects legacy /invoice/:public_id URLs to /receipt/:public_id for backward compatibility
 */
const InvoiceRedirect = () => {
  const { public_id } = useParams();
  return <Navigate to={`/receipt/${public_id}`} replace />;
};


/**
 * @function AppContent
 * @description Componente que configura los interceptores y define las rutas. Se separa para acceder al AuthContext.
 */
const AppContent = () => {
  const { user, business, isImpersonating } = React.useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Redirection logic removed to allow Freemium/Read-Only access
  // Users will see a banner instead if their setup is incomplete or subscription is pending
  React.useEffect(() => {
  }, [user, business, location, navigate]);

  // Los pageviews se capturan automáticamente vía `capture_pageview: 'history_change'`
  // (configurado en index.js); no hace falta capturarlos manualmente en cada navegación.

  return (
    <>
      {/* Banner flotante de impersonación - siempre visible cuando está activo */}
      <ImpersonationBanner />

      {/* Contenedor principal con padding ajustado cuando el banner está visible */}
      <div style={{ paddingTop: isImpersonating ? '48px' : '0' }}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/plans-comparison" element={<PlansComparisonPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/businesses/:businessSlug/register-client" element={<ClientSelfRegisterPage />} />
          <Route path="/business/:businessSlug" element={<PublicCatalogPage />} />

          {/* Quote public route */}
          <Route path="/quote/:public_id" element={<QuotePage />} />

          {/* Receipt route (new primary route) */}
          <Route path="/receipt/:public_id" element={<InvoicePage />} />
          <Route path="/expense-receipt/:public_id" element={<ExpenseReceiptPage />} />
          <Route path="/settlement-receipt/:public_id" element={<SettlementReceiptPage />} />
          <Route path="/delivery-slip/:public_id" element={<DeliverySlipPage />} />

          {/* Legacy invoice route with redirect for backward compatibility */}
          <Route
            path="/invoice/:public_id"
            element={<InvoiceRedirect />}
          />

          {/* Legal Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/dpa" element={<DpaPage />} />

          {/* Private routes wrapped with Layout */}
          <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/sales" element={<SalesPage />} />
            <Route path="/quick-print" element={<QuickPrintPage />} />
            <Route path="/production" element={<React.Suspense fallback={pageFallback}><ProductionBoardPage /></React.Suspense>} />
            <Route path="/quotes" element={<QuotesPage />} />
            <Route path="/products" element={<ProductsPage />} />

            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/suppliers" element={<SuppliersPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/incomes" element={<IncomesPage />} />
            <Route path="/settlements" element={<SupplierSettlementsPage />} />
            <Route path="/cash-register" element={<CashRegisterPage />} />
            <Route path="/payment-notifications" element={<PaymentNotificationsPage />} />
            <Route path="/settings" element={<Navigate to="/settings/profile" />} />
            <Route path="/settings/:section" element={<SettingsPage />} />
          </Route>

          {/* Admin Routes (Backoffice) */}
          <Route element={<PrivateRoute><React.Suspense fallback={<div className="p-5 text-center">Loading Admin...</div>}><AdminLayout /></React.Suspense></PrivateRoute>}>
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/businesses" element={<AdminBusinessesPage />} />
            <Route path="/admin/plans" element={<AdminPlansPage />} />
            <Route path="/admin/messages" element={<AdminContactMessagesPage />} />
            <Route path="/admin/impersonation-logs" element={<AdminImpersonationLogsPage />} />
            <Route path="/admin/ai-analyses" element={<AdminAiAnalysesPage />} />
            <Route path="/admin/appearance" element={<AdminAppearancePage />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
          </Route>

          {/* Redirect any unmatched routes to / if authenticated, or /login if not */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes >
      </div>
    </>
  );
};

/**
 * @function App
 * @description Contenedor raíz que envuelve la aplicación con el Router y el AuthProvider.
 */
function App() {
  return (
    <ErrorBoundary>
      {/* ThemeProvider va por fuera de AuthProvider a propósito: AuthProvider no
          renderiza children mientras carga y muestra un spinner a pantalla
          completa; si el tema quedara dentro, ese spinner saldría sin pintar. */}
      <ThemeProvider>
        <Router>
          <AuthProvider>
            <Toaster position="top-right" reverseOrder={false} />
            <div className="main-content-wrapper">
              <AppContent />
            </div>
            <Footer />
            <CookieConsentBanner />
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
